import i18n, { SUPPORTED_LANGUAGES, type SupportedLanguage } from './index';
import { api } from '../lib/api';

const LANG_STORAGE_KEY = 'i18nextLng';
const GEO_SEED_SENTINEL = 'geoLocaleApplied';

function isSupported(base: string): base is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(base);
}

function safeGetItem(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable (private mode / disabled) — no-op */
  }
}

/**
 * One-time geo-based language seed.
 *
 * Runs once on app start. It NEVER overrides an explicit user choice:
 *  - If `?lng=` is present in the URL, the user is explicitly requesting a
 *    language → skip.
 *  - If localStorage already has `i18nextLng`, a language has already been
 *    chosen/cached (by the user or a prior visit) → skip.
 *  - If our own sentinel `geoLocaleApplied` is set, we've already seeded once
 *    before → skip (so that even if the user later clears their explicit
 *    choice we don't re-seed, and we don't re-seed on every reload).
 *
 * Only when none of those signals exist do we fetch /geo/hint and, if the
 * suggested locale maps to a supported language different from the current
 * one, switch to it (which i18next persists to localStorage via its cache).
 */
export async function seedLocaleFromGeo(): Promise<void> {
  // Mark as attempted up-front so concurrent/repeat calls and future reloads
  // are no-ops regardless of the request outcome.
  if (safeGetItem(GEO_SEED_SENTINEL)) return;

  // Respect an explicit querystring choice (?lng=).
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('lng')) return;
  } catch {
    /* ignore malformed URL */
  }

  // Respect an existing cached/explicit choice.
  if (safeGetItem(LANG_STORAGE_KEY)) return;

  // From here on we will consume our single seeding opportunity.
  safeSetItem(GEO_SEED_SENTINEL, '1');

  try {
    const res = await api.get<{ suggestedLocale?: string }>('/geo/hint');
    const suggested = res.data?.suggestedLocale;
    if (!suggested) return;

    const base = suggested.split('-')[0]?.toLowerCase();
    if (!base || !isSupported(base)) return;

    const current = (i18n.language || '').split('-')[0]?.toLowerCase();
    if (base === current) return;

    await i18n.changeLanguage(base);
    // i18next's localStorage cache persists this, but set it explicitly too
    // in case detection caching is ever disabled.
    safeSetItem(LANG_STORAGE_KEY, base);
  } catch {
    /* offline / request failed — safe no-op */
  }
}
